import React, { createContext, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [role, setRole] = useState(null)
  const [profileLoading, setProfileLoading] = useState(false)
  const [error, setError] = useState(null)
  const [signingOut, setSigningOut] = useState(false)
  const navigate = useNavigate();

  // Separate async operations object
  const profileOperations = {
    async load(userId) {
      if (!userId) return
      setProfileLoading(true)
      try {
        const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single()
        
        if (!error && data) {
          setUserProfile(data)
        }
      } catch (error) {
        setError('Failed to load profile')
      } finally {
        setProfileLoading(false)
      }
    },
    
    clear() {
      setUserProfile(null)
      setProfileLoading(false)
    }
  }

  // Protected auth handlers
  const authStateHandlers = {
    // CRITICAL: This MUST remain synchronous
    onChange: (event, session) => {
      setUser(session?.user ?? null)
      setLoading(false)
      
      if (session?.user) {
        profileOperations?.load(session?.user?.id) // Fire-and-forget
        const derivedRole = session?.user?.user_metadata?.role || localStorage.getItem('userRole') || null
        if (derivedRole) {
          setRole(derivedRole)
          localStorage.setItem('userRole', derivedRole)
        }
      } else {
        profileOperations?.clear()
        setRole(null)
        localStorage.removeItem('userRole')
      }
    }
  }

  useEffect(() => {
    // Get initial session
    const getSession = async () => {
      try {
        const { data: { session }, error } = await supabase?.auth?.getSession()
        if (error) {
          setError(error?.message)
        } else {
          authStateHandlers?.onChange(null, session)
        }
      } catch (error) {
        setError('Failed to get session')
        setLoading(false)
      }
    };

    getSession()

    // Listen for auth changes
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      authStateHandlers?.onChange
    )

    return () => subscription?.unsubscribe()
  }, [])

  // Auth methods
  const signIn = async (email, password) => {
    try {
      setError(null)
      const { data, error } = await supabase?.auth?.signInWithPassword({ email, password })

      if (error) {
        if (error?.message?.includes('Failed to fetch') || 
            error?.message?.includes('AuthRetryableFetchError')) {
          setError('Cannot connect to authentication service. Your Supabase project may be paused or inactive. Please check your Supabase dashboard and resume your project if needed.')
        } else {
          setError(error?.message)
        }
        return { error }
      }

  // Persist role if present in metadata (fallback partner kept for compat)
  const metaRole = data?.user?.user_metadata?.role || 'partner'
  setRole(metaRole)
  localStorage.setItem('userRole', metaRole)
  return { data }
    } catch (error) {
      if (error?.message?.includes('Failed to fetch')) {
        setError('Cannot connect to authentication service. Your Supabase project may be paused or inactive.')
      } else {
        setError('An unexpected error occurred')
      }
      return { error: { message: 'An unexpected error occurred' } }
    }
  }

  const signUp = async (email, password, userData = {}) => {
    try {
      setError(null)
      const { data, error } = await supabase?.auth?.signUp({
        email,
        password,
        options: {
          data: userData
        }
      })

      if (error) {
        setError(error?.message)
        return { error }
      }

  // For sign up we may receive user with metadata directly
  const metaRole = data?.user?.user_metadata?.role || userData?.role || 'partner'
  setRole(metaRole)
  localStorage.setItem('userRole', metaRole)
  return { data }
    } catch (error) {
      setError('An unexpected error occurred')
      return { error: { message: 'An unexpected error occurred' } }
    }
  }

  const signOut = async () => {
    if (signingOut) return;
    setSigningOut(true);
    try {
      setError(null)
      await supabase?.auth?.signOut()
    } catch (e) {
      // ignore network errors for signOut – we'll still clear client state
    }
    // Clear all auth-related client state regardless of Supabase response
    setUser(null)
    setUserProfile(null)
    setRole(null)
    profileOperations?.clear()
    localStorage.removeItem('authToken')
    localStorage.removeItem('userRole')
    // Redirection SPA contrôlée
    navigate('/', { replace: true });
    // Dispatch a custom event so components can react immediately
    window.dispatchEvent(new Event('app:loggedOut'))
    setSigningOut(false)
  }

  const updateProfile = async (updates) => {
    if (!user) return { error: { message: 'No user logged in' } }
    
    try {
      const { data, error } = await supabase?.from('user_profiles')?.update(updates)?.eq('id', user?.id)?.select()?.single()
      if (!error) setUserProfile(data)
      return { data, error }
    } catch (error) {
      return { error: { message: 'Network error. Please try again.' } }
    }
  }

  const value = {
    user,
  userProfile,
    loading,
    profileLoading,
    error,
  role,
    signIn,
    signUp,
    signOut,
    updateProfile,
  isAuthenticated: !!user,
  signingOut
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}